import { useMutation, useQuery } from "@tanstack/react-query";
import { useNavigate, useRouteContext } from "@tanstack/react-router";
import { Check, Copy } from "lucide-react";
import { type ReactNode, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
	AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { flushAnalytics, track } from "@/lib/analytics";
import { authClient, invalidateSession } from "@/lib/auth-client";
import { getDesktopBridge } from "@/lib/desktop";
import { downloadJson } from "@/lib/download-json";
import { env } from "@/lib/env";
import { queryClient, trpc, trpcClient } from "@/lib/trpc";

/** Free plucks a month, shared by every copy mode (mirrors the API). */
const FREE_PLUCK_LIMIT = 50;
const COPIED_RESET_MS = 2000;
const FULL_PERCENT = 100;

function formatDate(value: string | Date): string {
	return new Date(value).toLocaleDateString("en-US", {
		month: "short",
		day: "numeric",
		year: "numeric",
	});
}

export function AccountPage() {
	const { user } = useRouteContext({ from: "/app" });

	return (
		<div className="mx-auto flex max-w-3xl flex-col gap-8 p-8">
			<header>
				<h1 className="font-display font-extrabold text-2xl tracking-[-0.02em]">
					Account
				</h1>
				<p className="text-muted-foreground text-sm">
					{user.email} · plan, usage, referrals and your data.
				</p>
			</header>
			<MarketingPromptBanner />
			<PlanSection />
			<ReferralSection />
			<GenerationSection />
			<EmailPreferencesSection />
			<AgentAccessSection />
			<DataSection email={user.email} />
		</div>
	);
}

function Section({
	title,
	description,
	children,
}: {
	children: ReactNode;
	description?: string;
	title: string;
}) {
	return (
		<section className="flex flex-col gap-4">
			<div>
				<h2 className="font-bold font-display text-[17px] tracking-[-0.01em]">
					{title}
				</h2>
				{description && (
					<p className="text-muted-foreground text-sm">{description}</p>
				)}
			</div>
			{children}
		</section>
	);
}

function Card({
	children,
	className,
}: {
	children: ReactNode;
	className?: string;
}) {
	return (
		<div className={`rounded-xl border bg-card p-5 ${className ?? ""}`}>
			{children}
		</div>
	);
}

// ── Plan & usage ────────────────────────────────────────────────

type BillingInterval = "month" | "year";

const SOURCE_NOTES: Record<string, string> = {
	grant: "Granted by the Pluck team",
	referral: "From a referral reward",
	apple: "Billed through the App Store",
};

async function startCheckout(
	slug: string,
	context: {
		currentPlan: string | undefined;
		kind: "plan" | "topup";
		plan?: string;
	}
): Promise<void> {
	track("billing.checkout_clicked", {
		slug,
		kind: context.kind,
		plan: context.plan,
		current_plan: context.currentPlan,
	});
	try {
		await authClient.checkout({ slug });
	} catch (err) {
		track("billing.checkout_failed", { slug, kind: context.kind });
		toast.error(err instanceof Error ? err.message : "Checkout unavailable");
	}
}

async function openPortal(
	reason: "manage" | "switch_plan",
	plan?: string
): Promise<void> {
	track("billing.portal_opened", { reason, plan });
	try {
		await authClient.customer.portal();
	} catch (err) {
		track("billing.portal_failed", { reason });
		toast.error(err instanceof Error ? err.message : "Portal unavailable");
	}
}

function formatCredits(value: number): string {
	return value.toLocaleString("en-US");
}

function formatPrice(cents: number): string {
	return `$${(cents / 100).toLocaleString("en-US", { maximumFractionDigits: 0 })}`;
}

type BillingSummary = Awaited<
	ReturnType<typeof trpcClient.billing.summary.query>
>;
type BillingCatalog = Awaited<
	ReturnType<typeof trpcClient.billing.plans.query>
>;

function CurrentPlanCard({ summary }: { summary: BillingSummary }) {
	const { plan, subscription, source, grantExpiresAt } = summary;
	return (
		<Card className="flex flex-col gap-1">
			<p className="text-muted-foreground text-sm">Current plan</p>
			<p className="font-display font-extrabold text-2xl tracking-[-0.02em]">
				{plan.name}
			</p>
			{subscription && (
				<p className="text-muted-foreground text-xs">
					{subscription.cancelAtPeriodEnd ? "Ends" : "Renews"}{" "}
					{formatDate(subscription.currentPeriodEnd)}
				</p>
			)}
			{SOURCE_NOTES[source] && (
				<p className="text-muted-foreground text-xs">
					{SOURCE_NOTES[source]}
					{grantExpiresAt && source === "grant"
						? ` until ${formatDate(grantExpiresAt)}`
						: ""}
				</p>
			)}
			{source === "polar" && (
				<Button
					className="mt-3 w-fit"
					onClick={() => openPortal("manage", plan.id)}
					size="sm"
					variant="outline"
				>
					Manage subscription
				</Button>
			)}
		</Card>
	);
}

function CreditsCard({
	summary,
	topUp,
}: {
	summary: BillingSummary;
	topUp: BillingCatalog["topUp"] | undefined;
}) {
	const { plan, credits } = summary;
	return (
		<Card className="flex flex-col gap-1">
			<p className="text-muted-foreground text-sm">Generation credits</p>
			<p className="font-display font-extrabold text-2xl tracking-[-0.02em]">
				{formatCredits(credits.balance)}
			</p>
			<p className="text-muted-foreground text-xs">
				{plan.monthlyCredits > 0
					? `${formatCredits(plan.monthlyCredits)} included every month`
					: "Generate on Pluck's servers with a paid plan. Local Codex in Pluck Desktop is always free."}
			</p>
			{credits.nextExpiry?.at && (
				<p className="text-muted-foreground text-xs">
					{formatCredits(credits.nextExpiry.amount)} expire{" "}
					{formatDate(credits.nextExpiry.at)}
				</p>
			)}
			{plan.canTopUp && topUp?.available && (
				<Button
					className="mt-3 w-fit"
					onClick={() =>
						startCheckout(topUp.slug, {
							kind: "topup",
							currentPlan: plan.id,
						})
					}
					size="sm"
					variant="outline"
				>
					Buy {formatCredits(topUp.credits)} credits · $10
				</Button>
			)}
		</Card>
	);
}

function PlanOfferCard({
	offer,
	interval,
	currentPlanId,
	hasPolarPlan,
}: {
	currentPlanId: string | undefined;
	hasPolarPlan: boolean;
	interval: BillingInterval;
	offer: BillingCatalog["plans"][number];
}) {
	const slug = interval === "month" ? offer.monthlySlug : offer.yearlySlug;
	const price = interval === "month" ? offer.priceCents : offer.priceCents * 10;
	let action = (
		<Button
			disabled={!offer.available}
			onClick={() =>
				startCheckout(slug, {
					kind: "plan",
					plan: offer.id,
					currentPlan: currentPlanId,
				})
			}
			size="sm"
			variant={offer.id === "pro" ? "default" : "outline"}
		>
			{offer.available ? `Get ${offer.name}` : "Coming soon"}
		</Button>
	);
	if (currentPlanId === offer.id) {
		action = (
			<Button disabled size="sm" variant="outline">
				Current plan
			</Button>
		);
	} else if (hasPolarPlan) {
		// Plan changes go through Polar so they're prorated.
		action = (
			<Button
				onClick={() => openPortal("switch_plan", offer.id)}
				size="sm"
				variant="outline"
			>
				Switch to {offer.name}
			</Button>
		);
	}
	return (
		<Card
			className={`flex flex-col gap-3 ${offer.id === "pro" ? "border-foreground/40" : ""}`}
		>
			<div>
				<p className="font-medium">{offer.name}</p>
				<p className="mt-1 font-display font-extrabold text-2xl tracking-[-0.02em]">
					{formatPrice(price)}
					<span className="font-normal text-muted-foreground text-sm">
						/{interval === "month" ? "mo" : "yr"}
					</span>
				</p>
			</div>
			<ul className="flex flex-col gap-1 text-muted-foreground text-xs">
				<li>{formatCredits(offer.monthlyCredits)} credits every month</li>
				<li>Unlimited plucks in the extension</li>
				<li>Credit top-ups</li>
				{offer.priority && <li>Priority generation queue</li>}
			</ul>
			<div className="mt-auto">{action}</div>
		</Card>
	);
}

function IntervalToggle({
	value,
	onChange,
}: {
	onChange: (value: BillingInterval) => void;
	value: BillingInterval;
}) {
	return (
		<fieldset className="flex rounded-md bg-muted p-0.5 text-xs">
			<legend className="sr-only">Billing period</legend>
			{(["month", "year"] as const).map((option) => (
				<label
					className={`cursor-pointer rounded-sm px-3 py-1 ${value === option ? "bg-background font-medium shadow-xs" : "text-muted-foreground"}`}
					key={option}
				>
					<input
						checked={value === option}
						className="sr-only"
						name="billing-interval"
						onChange={() => onChange(option)}
						type="radio"
					/>
					{option === "month" ? "Monthly" : "Yearly · 2 months free"}
				</label>
			))}
		</fieldset>
	);
}

function CreditHistory() {
	const history = useQuery(trpc.billing.history.queryOptions());
	if ((history.data?.length ?? 0) === 0) {
		return null;
	}
	return (
		<Card className="flex flex-col gap-2">
			<p className="font-medium text-sm">Recent credit activity</p>
			<ul className="flex flex-col divide-y text-xs">
				{history.data?.slice(0, 10).map((row) => (
					<li className="flex items-center justify-between py-1.5" key={row.id}>
						<span className="text-muted-foreground">
							{row.note ?? row.kind} · {formatDate(row.createdAt)}
						</span>
						<span className={row.delta > 0 ? "text-emerald-600" : ""}>
							{row.delta > 0 ? "+" : ""}
							{formatCredits(row.delta)}
						</span>
					</li>
				))}
			</ul>
		</Card>
	);
}

function PlanSection() {
	const usage = useQuery(trpc.getUsage.queryOptions());
	const summary = useQuery(trpc.billing.summary.queryOptions());
	const catalog = useQuery(trpc.billing.plans.queryOptions());
	const [interval, setBillingInterval] = useState<BillingInterval>("month");
	const unlimitedPlucks = summary.data?.plan.extensionUnlimited ?? false;
	const hasPolarPlan =
		summary.data?.source === "polar" &&
		summary.data.plan.id !== "legacy_unlimited";

	// Once per visit, when both the plan and the offers have loaded.
	const viewed = useRef(false);
	const currentPlan = summary.data?.plan.id;
	const planSource = summary.data?.source;
	const balance = summary.data?.credits.balance;
	const offers = catalog.data?.plans.length;
	useEffect(() => {
		if (viewed.current || currentPlan === undefined || offers === undefined) {
			return;
		}
		viewed.current = true;
		track("billing.plans_viewed", {
			current_plan: currentPlan,
			plan_source: planSource,
			credit_balance: balance,
			offers,
		});
	}, [currentPlan, planSource, balance, offers]);

	const changeInterval = (next: BillingInterval) => {
		setBillingInterval(next);
		track("billing.interval_changed", { interval: next });
	};

	return (
		<Section title="Plan & usage">
			<div className="grid gap-4 sm:grid-cols-2">
				{summary.data ? (
					<>
						<CurrentPlanCard summary={summary.data} />
						<CreditsCard summary={summary.data} topUp={catalog.data?.topUp} />
					</>
				) : (
					<>
						<Skeleton className="h-36 rounded-xl" />
						<Skeleton className="h-36 rounded-xl" />
					</>
				)}
				<UsageCard
					count={usage.data?.total.count ?? 0}
					hasUnlimited={unlimitedPlucks}
					label="Plucks this month"
					limit={usage.data?.total.limit ?? FREE_PLUCK_LIMIT}
				/>
			</div>
			<div className="flex items-center justify-between">
				<p className="font-medium text-sm">Plans</p>
				<IntervalToggle onChange={changeInterval} value={interval} />
			</div>
			<div className="grid gap-4 sm:grid-cols-3">
				{(catalog.data?.plans ?? []).map((offer) => (
					<PlanOfferCard
						currentPlanId={summary.data?.plan.id}
						hasPolarPlan={hasPolarPlan}
						interval={interval}
						key={offer.id}
						offer={offer}
					/>
				))}
			</div>
			<CreditHistory />
		</Section>
	);
}

function UsageCard({
	label,
	count,
	limit,
	hasUnlimited,
}: {
	count: number;
	hasUnlimited: boolean;
	label: string;
	limit: number | null;
}) {
	const cap = limit ?? 0;
	const atLimit = !hasUnlimited && cap > 0 && count >= cap;
	useEffect(() => {
		if (atLimit) {
			track("usage.limit_viewed", { limit: cap });
		}
	}, [atLimit, cap]);
	const percent =
		hasUnlimited || cap === 0
			? 0
			: Math.min(FULL_PERCENT, (count / cap) * FULL_PERCENT);
	return (
		<Card className="sm:col-span-2">
			<p className="text-muted-foreground text-sm">{label}</p>
			<p className="mt-1 font-display font-extrabold text-2xl tracking-[-0.02em]">
				{hasUnlimited ? count : `${count} / ${cap}`}
			</p>
			{!hasUnlimited && (
				<div className="mt-3 h-1.5 overflow-hidden rounded-full bg-muted">
					<div
						className={`h-full rounded-full ${atLimit ? "bg-destructive" : "bg-foreground"}`}
						style={{ width: `${percent}%` }}
					/>
				</div>
			)}
			{atLimit && (
				<p className="mt-2 text-destructive text-xs">
					Monthly limit reached. Upgrade for unlimited plucks.
				</p>
			)}
		</Card>
	);
}

// ── Referral ────────────────────────────────────────────────────

function ReferralSection() {
	const referral = useQuery(trpc.referral.getMyReferralInfo.queryOptions());
	const [copied, setCopied] = useState(false);
	const info = referral.data;
	if (!info) {
		return null;
	}
	const copy = async () => {
		track("referral.link_copied", {
			from: "account",
			referrals: info.totalReferrals,
			rewarded: info.rewardedReferrals,
		});
		await navigator.clipboard.writeText(info.link);
		setCopied(true);
		setTimeout(() => setCopied(false), COPIED_RESET_MS);
	};
	return (
		<Section
			description="When a friend makes their first pluck, you get a free month of Unlimited."
			title="Refer & earn"
		>
			<Card>
				<div className="flex items-center gap-2">
					<code className="flex-1 truncate rounded-md bg-muted px-3 py-2 text-xs">
						{info.link}
					</code>
					<Button onClick={copy} size="sm" variant="outline">
						{copied ? <Check /> : <Copy />}
						{copied ? "Copied" : "Copy"}
					</Button>
				</div>
				<p className="mt-3 text-muted-foreground text-xs">
					{info.totalReferrals} referred · {info.rewardedReferrals} reward
					{info.rewardedReferrals === 1 ? "" : "s"} earned
					{info.activeRewardExpiresAt &&
						` · active until ${formatDate(info.activeRewardExpiresAt)}`}
				</p>
			</Card>
		</Section>
	);
}

// ── Email ───────────────────────────────────────────────────────

function invalidatePrefs() {
	return queryClient.invalidateQueries({
		queryKey: trpc.account.getEmailPreferences.queryKey(),
	});
}

function MarketingPromptBanner() {
	const prefs = useQuery(trpc.account.getEmailPreferences.queryOptions());
	const [dismissed, setDismissed] = useState(false);
	const setOptIn = useMutation(
		trpc.account.setMarketingOptIn.mutationOptions()
	);
	const dismiss = useMutation(
		trpc.account.dismissMarketingPrompt.mutationOptions()
	);
	if (dismissed || !prefs.data || prefs.data.marketingPromptSeen) {
		return null;
	}
	const answer = async (optIn: boolean) => {
		setDismissed(true);
		track("account.marketing_prompt_answered", { opt_in: optIn });
		await Promise.all([
			optIn ? setOptIn.mutateAsync({ optIn: true }) : Promise.resolve(),
			dismiss.mutateAsync(),
		]);
		await invalidatePrefs();
	};
	return (
		<div className="flex flex-col gap-3 rounded-xl border bg-card p-4 sm:flex-row sm:items-center sm:justify-between">
			<p className="text-sm">
				<span className="font-medium">Want occasional product updates?</span>{" "}
				<span className="text-muted-foreground">
					A short note when there's something genuinely useful. No spam.
				</span>
			</p>
			<div className="flex shrink-0 gap-2">
				<Button onClick={() => answer(false)} size="sm" variant="outline">
					No thanks
				</Button>
				<Button onClick={() => answer(true)} size="sm">
					Yes please
				</Button>
			</div>
		</div>
	);
}

// ── Image generation ────────────────────────────────────────────

function GenerationSection() {
	const settings = useQuery(trpc.account.getGenerationSettings.queryOptions());
	const [instructions, setInstructions] = useState<string | null>(null);
	const save = useMutation(
		trpc.account.setGenerationSettings.mutationOptions({
			onSuccess: () => {
				queryClient
					.invalidateQueries({
						queryKey: trpc.account.getGenerationSettings.queryKey(),
					})
					.catch(() => {
						/* refreshes on next visit */
					});
			},
			onError: (err) => toast.error(err.message),
		})
	);
	if (!settings.data) {
		return null;
	}
	const { template, templates } = settings.data;
	const draft = instructions ?? settings.data.instructions;
	const draftChanged = draft.trim() !== settings.data.instructions.trim();

	return (
		<Section
			description="How Pluck phrases your prompts for the image model. Applies to new generations on every board."
			title="Image generation"
		>
			<Card className="flex flex-col gap-3">
				<fieldset className="flex flex-col gap-2">
					<legend className="sr-only">Prompt template</legend>
					{templates.map((option) => (
						<label
							className="flex cursor-pointer items-start gap-3 rounded-lg border p-3 has-[:checked]:border-primary has-[:checked]:bg-primary/5"
							htmlFor={`template-${option.id}`}
							key={option.id}
						>
							<input
								checked={template === option.id}
								className="mt-1"
								disabled={save.isPending}
								id={`template-${option.id}`}
								name="prompt-template"
								onChange={() => {
									save.mutate({ template: option.id });
									track("settings.template_changed", {
										template: option.id,
										from: template,
									});
								}}
								type="radio"
								value={option.id}
							/>
							<span className="flex flex-col gap-0.5">
								<span className="font-medium text-sm">
									{option.label}
									{option.id === "generic" && (
										<span className="ml-2 text-muted-foreground text-xs">
											Default
										</span>
									)}
								</span>
								<span className="text-muted-foreground text-xs">
									{option.description}
								</span>
							</span>
						</label>
					))}
				</fieldset>
				{template === "custom" && (
					<form
						className="flex flex-col gap-2"
						onSubmit={(e) => {
							e.preventDefault();
							track("settings.instructions_saved", {
								length: draft.trim().length,
								was_empty: settings.data.instructions.trim().length === 0,
							});
							save.mutate(
								{ template: "custom", instructions: draft },
								{ onSuccess: () => setInstructions(null) }
							);
						}}
					>
						<Label htmlFor="custom-instructions">Your instructions</Label>
						<Textarea
							id="custom-instructions"
							maxLength={4000}
							onChange={(e) => setInstructions(e.target.value)}
							placeholder="e.g. Render everything as a clean product photo on a white background."
							rows={4}
							value={draft}
						/>
						<p className="text-muted-foreground text-xs">
							Sent before your prompt and the list of connected images.
						</p>
						<Button
							className="w-fit"
							disabled={!draftChanged || save.isPending}
							size="sm"
							type="submit"
						>
							Save instructions
						</Button>
					</form>
				)}
			</Card>
		</Section>
	);
}

function EmailPreferencesSection() {
	const prefs = useQuery(trpc.account.getEmailPreferences.queryOptions());
	const setOptIn = useMutation(
		trpc.account.setMarketingOptIn.mutationOptions({
			onSuccess: invalidatePrefs,
		})
	);
	return (
		<Section title="Email preferences">
			<Card className="flex items-start justify-between gap-4">
				<div>
					<Label htmlFor="marketing-opt-in">Product updates</Label>
					<p className="mt-1 text-muted-foreground text-sm">
						Occasional tips and news. Transactional emails (sign-in links,
						billing) are sent regardless.
					</p>
				</div>
				<Switch
					checked={prefs.data?.marketingOptIn ?? false}
					disabled={prefs.isPending || setOptIn.isPending}
					id="marketing-opt-in"
					onCheckedChange={(next) => {
						setOptIn.mutate({ optIn: next });
					}}
				/>
			</Card>
		</Section>
	);
}

// ── Data & deletion ─────────────────────────────────────────────

// ── Agent access ────────────────────────────────────────────────

/** Where Pluck Desktop is normally installed on macOS (used on the web). */
const DEFAULT_MCP_LAUNCH = {
	command: "/Applications/Pluck Studio.app/Contents/MacOS/Pluck Studio",
	args: ["/Applications/Pluck Studio.app/Contents/Resources/pluck-mcp.mjs"],
	env: { ELECTRON_RUN_AS_NODE: "1" } as Record<string, string>,
};

const quote = (value: string) => `"${value.replaceAll('"', '\\"')}"`;

function setupCommands(
	launch: typeof DEFAULT_MCP_LAUNCH,
	token: string
): { label: string; command: string }[] {
	const launchEnv = { ...launch.env, PLUCK_TOKEN: token };
	const target = [launch.command, ...launch.args].map(quote).join(" ");
	const claudeEnv = Object.entries(launchEnv)
		.map(([k, v]) => `-e ${k}=${v}`)
		.join(" ");
	const codexEnv = Object.entries(launchEnv)
		.map(([k, v]) => `--env ${k}=${v}`)
		.join(" ");
	return [
		{
			label: "Claude Code",
			command: `claude mcp add pluck --scope user ${claudeEnv} -- ${target}`,
		},
		{ label: "Codex", command: `codex mcp add pluck ${codexEnv} -- ${target}` },
	];
}

function CopyableCommand({
	label,
	command,
	kind,
}: {
	command: string;
	/** Which command, for analytics (never the command itself). */
	kind: string;
	label: string;
}) {
	const [copied, setCopied] = useState(false);
	return (
		<div className="flex flex-col gap-1">
			<p className="font-medium text-xs">{label}</p>
			<div className="flex items-start gap-2">
				<code className="min-w-0 flex-1 break-all rounded-md bg-muted px-2 py-1.5 text-[11px]">
					{command}
				</code>
				<Button
					aria-label={`Copy ${label} command`}
					onClick={async () => {
						track("agent_access.command_copied", { command: kind });
						await navigator.clipboard.writeText(command);
						setCopied(true);
						setTimeout(() => setCopied(false), COPIED_RESET_MS);
					}}
					size="icon-sm"
					variant="outline"
				>
					{copied ? <Check /> : <Copy />}
				</Button>
			</div>
		</div>
	);
}

function AgentAccessSection() {
	const tokens = useQuery(trpc.tokens.list.queryOptions());
	const [name, setName] = useState("Claude Code");
	const [created, setCreated] = useState<{
		name: string;
		token: string;
	} | null>(null);
	const [launch, setLaunch] = useState(DEFAULT_MCP_LAUNCH);

	useEffect(() => {
		getDesktopBridge()
			?.mcpLaunchCommand()
			.then(setLaunch)
			.catch(() => {
				/* keep the default paths */
			});
	}, []);

	const refresh = () =>
		queryClient.invalidateQueries({ queryKey: trpc.tokens.list.queryKey() });
	const create = useMutation(
		trpc.tokens.create.mutationOptions({
			onSuccess: (row) => {
				setCreated({ name: row.name, token: row.token });
				refresh().catch(() => {
					/* list refreshes on next visit */
				});
			},
			onError: (err) => toast.error(err.message),
		})
	);
	const revoke = useMutation(
		trpc.tokens.revoke.mutationOptions({
			onSuccess: () => {
				refresh().catch(() => {
					/* list refreshes on next visit */
				});
			},
		})
	);

	const mcpUrl = `${env.VITE_SERVER_URL}/mcp`;
	return (
		<Section
			description="Let Claude, Cursor, Codex or other MCP agents read and edit your boards and library. Changes show up live in Pluck Studio."
			title="Agent access"
		>
			<Card className="flex flex-col gap-3">
				<p className="text-sm">
					Add Pluck to any app that supports remote MCP servers with this URL.
					You'll sign in to Pluck and approve the app; no token needed.
				</p>
				<CopyableCommand
					command={mcpUrl}
					kind="mcp_url"
					label="MCP server URL"
				/>
				<CopyableCommand
					command={`claude mcp add --transport http pluck ${mcpUrl}`}
					kind="claude_code_remote"
					label="Claude Code"
				/>
			</Card>
			<Card className="flex flex-col gap-3">
				<p className="text-muted-foreground text-xs">
					Or create an access token to run Pluck's MCP server locally, through
					Pluck Desktop.
				</p>
				<form
					className="flex items-end gap-2"
					onSubmit={(e) => {
						e.preventDefault();
						if (name.trim()) {
							create.mutate({ name: name.trim() });
						}
					}}
				>
					<div className="flex min-w-0 flex-1 flex-col gap-1">
						<Label htmlFor="token-name">New token name</Label>
						<Input
							id="token-name"
							maxLength={80}
							onChange={(e) => setName(e.target.value)}
							value={name}
						/>
					</div>
					<Button disabled={create.isPending || !name.trim()} type="submit">
						Create token
					</Button>
				</form>
				{created && (
					<div className="flex flex-col gap-3 rounded-lg border border-amber-500/40 bg-amber-500/5 p-3">
						<p className="text-sm">
							Token for <span className="font-medium">{created.name}</span>.
							Copy a command now: the token isn't shown again.
						</p>
						{setupCommands(launch, created.token).map((c) => (
							<CopyableCommand
								command={c.command}
								key={c.label}
								kind={c.label === "Codex" ? "codex_local" : "claude_code_local"}
								label={c.label}
							/>
						))}
						<p className="text-muted-foreground text-xs">
							Needs Pluck Desktop installed (the commands use the app as the
							tool server). Then ask your agent to "list my Pluck boards".
						</p>
					</div>
				)}
			</Card>
			{(tokens.data?.length ?? 0) > 0 && (
				<Card className="flex flex-col divide-y p-0">
					{tokens.data?.map((t) => (
						<div className="flex items-center gap-3 px-5 py-3" key={t.id}>
							<div className="min-w-0 flex-1">
								<p className="truncate font-medium text-sm">{t.name}</p>
								<p className="text-muted-foreground text-xs">
									{t.prefix}… · created {formatDate(t.createdAt)}
									{t.lastUsedAt
										? ` · last used ${formatDate(t.lastUsedAt)}`
										: " · never used"}
								</p>
							</div>
							<Button
								disabled={revoke.isPending}
								onClick={() => revoke.mutate({ id: t.id })}
								size="sm"
								variant="outline"
							>
								Revoke
							</Button>
						</div>
					))}
				</Card>
			)}
		</Section>
	);
}

function DataSection({ email }: { email: string }) {
	const canDelete = useQuery(trpc.account.canDelete.queryOptions());
	const [exporting, setExporting] = useState(false);

	const exportData = async () => {
		setExporting(true);
		try {
			downloadJson(
				await trpcClient.account.exportMine.query(),
				"pluck-account"
			);
			track("account.data_exported", { outcome: "succeeded" });
		} catch (err) {
			track("account.data_exported", { outcome: "failed" });
			toast.error(err instanceof Error ? err.message : "Export failed");
		} finally {
			setExporting(false);
		}
	};

	const blocked = canDelete.data?.canDelete === false;

	return (
		<Section title="Your data">
			<Card>
				<p className="font-medium text-sm">Download your data</p>
				<p className="mt-1 text-muted-foreground text-sm">
					Everything associated with your account, as a JSON file.
				</p>
				<Button
					className="mt-3"
					disabled={exporting}
					onClick={exportData}
					size="sm"
					variant="outline"
				>
					{exporting ? "Preparing…" : "Download (.json)"}
				</Button>
			</Card>
			<Card className="border-destructive/30">
				<p className="font-medium text-sm">Delete account</p>
				<p className="mt-1 text-muted-foreground text-sm">
					Permanently removes your profile, library, boards, generations,
					referral data and billing records. This cannot be undone.
				</p>
				{blocked ? (
					<p className="mt-3 text-sm">
						You have an active subscription. Cancel it from the billing portal
						(under Plan &amp; usage) before deleting your account.
					</p>
				) : (
					<div className="mt-4">
						<DeleteAccountDialog disabled={canDelete.isPending} email={email} />
					</div>
				)}
			</Card>
		</Section>
	);
}

function DeleteAccountDialog({
	email,
	disabled,
}: {
	disabled?: boolean;
	email: string;
}) {
	const navigate = useNavigate();
	const [open, setOpen] = useState(false);
	const [confirmEmail, setConfirmEmail] = useState("");
	const [acknowledged, setAcknowledged] = useState(false);

	const remove = useMutation(
		trpc.account.deleteMine.mutationOptions({
			onSuccess: async () => {
				await flushAnalytics();
				setOpen(false);
				toast.success("Account deleted");
				await authClient.signOut();
				invalidateSession();
				queryClient.clear();
				await navigate({ to: "/login", search: { redirect: undefined } });
			},
			onError: (err) => toast.error(err.message),
		})
	);

	const canSubmit =
		acknowledged && confirmEmail.trim().toLowerCase() === email.toLowerCase();

	const onOpenChange = (next: boolean) => {
		setOpen(next);
		if (next) {
			track("account.delete_dialog_opened");
		} else {
			setConfirmEmail("");
			setAcknowledged(false);
		}
	};

	return (
		<AlertDialog onOpenChange={onOpenChange} open={open}>
			<AlertDialogTrigger asChild>
				<Button disabled={disabled} size="sm" variant="destructive">
					Delete account
				</Button>
			</AlertDialogTrigger>
			<AlertDialogContent>
				<AlertDialogHeader>
					<AlertDialogTitle>Permanently delete your account?</AlertDialogTitle>
					<AlertDialogDescription>
						This deletes your profile, sessions, library, boards, generations,
						referral data, billing records and mailing-list contact. There is no
						way to undo it.
					</AlertDialogDescription>
				</AlertDialogHeader>
				<div className="flex flex-col gap-3 py-1">
					<div className="flex flex-col gap-1.5">
						<Label htmlFor="delete-email">
							Type <span className="font-mono">{email}</span> to confirm
						</Label>
						<Input
							autoComplete="off"
							id="delete-email"
							onChange={(e) => setConfirmEmail(e.target.value)}
							placeholder={email}
							type="email"
							value={confirmEmail}
						/>
					</div>
					<label className="flex items-start gap-2 text-sm">
						<input
							checked={acknowledged}
							className="mt-0.5 size-4 accent-black"
							onChange={(e) => setAcknowledged(e.target.checked)}
							type="checkbox"
						/>
						I understand this is permanent and cannot be undone.
					</label>
				</div>
				<AlertDialogFooter>
					<AlertDialogCancel>Cancel</AlertDialogCancel>
					<AlertDialogAction
						className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
						disabled={!canSubmit || remove.isPending}
						onClick={(e) => {
							e.preventDefault();
							remove.mutate();
						}}
					>
						{remove.isPending ? "Deleting…" : "Delete account"}
					</AlertDialogAction>
				</AlertDialogFooter>
			</AlertDialogContent>
		</AlertDialog>
	);
}
